import type Phaser from "phaser";
import { NineSlicePanel, NineSliceButton, NINE_SLICE_BUTTON_EVENT, RectMask } from "@yjsgames/phaser-ui";
import { resolvePanelContentRect } from "@yjsgames/phaser-ui/panel-content";
import type { TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { type ShellLayout } from "../data/balance.js";
import { DEPTH } from "../objects/depthLayers.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import {
  computeExpandedTop,
  computeGameviewBackgroundRect,
  computeGameviewRect,
  GAMEVIEW_BACKGROUND_INSET,
  GAMEVIEW_EXPAND_FRACTION,
  type GameviewBackgroundRect,
  type GameviewRect,
} from "./gameviewLayout.js";
import { CONTENT_PANEL_STYLE, PRIMARY_BUTTON_STYLE, TAB_INNER_PADDING_X, TAB_INNER_PADDING_Y } from "./uiTokens.js";
import { HudView } from "./HudView.js";
import { AimLineView } from "./AimLineView.js";
import { DungeonTimerBar } from "./DungeonTimerBar.js";
import { BattlePressureBar } from "./BattlePressureBar.js";
import { BattleDeadlineLine } from "./BattleDeadlineLine.js";
import { BottomTabView, type TabId } from "./BottomTabView.js";
import { GuideQuestPanel, type GuideQuestPanelData } from "./GuideQuestPanel.js";
import { computeGuideQuestPanelRect } from "./guideQuestSlotLayout.js";
import { GameviewScrollingBackground } from "./GameviewScrollingBackground.js";
import { createPlayerShipDestructionFx, type PlayerShipDestructionFx } from "./playerShipDestructionFx.js";
import { BattleDialogueOverlay, type BattleDialogueData } from "./BattleDialogueOverlay.js";
import type { DialogueOverlayRect } from "./battleDialogueOverlayLayout.js";

export interface ShellFrameDeps {
  onSettingsClick: () => void;
  accountNeedsLink: boolean;
  onTabSelected: (tab: TabId) => void;
  enabledTabs: ReadonlySet<TabId>;
  readonly layout: ShellLayout;
  onToggleExpand: () => void;
  onHelp: () => void;
  onGuideQuestClaim: () => void;
  onGuideQuestGuide: () => void;
  onBattleDialogueComplete: (dialogueId: string) => void;
}

export const SHELL_EXPAND_FRACTION = GAMEVIEW_EXPAND_FRACTION;
export interface ShellWorldVisual {
  readonly bgTileKey: string;
}
const FOLDER_TAB_W = 56;
const FOLDER_TAB_H = 18;
const HELP_TAB_W = 26;
const HELP_TAB_H = 18;
const LAUNCHER_SHIP_SIZE = 40;
const PLAYER_SHIP_SCALE_TWEEN_MS = 180;
const BATTLE_PRESSURE_RAIL_FRAME_OFFSET_X = -8;
const BATTLE_PRESSURE_RAIL_PAD_Y = 8;

export class ShellFrame {
  /** 게임뷰 interior 클리핑 마스크 — 동적 오브젝트(벽돌/발사체)는 인스턴스마다 apply (Group setMask 미전파). */
  readonly gameviewMask: RectMask;
  readonly aimLine: AimLineView;
  private readonly launcherShip: Phaser.GameObjects.Image;
  private readonly launcherY0: number;
  readonly hud: HudView;
  readonly dungeonTimerBar: DungeonTimerBar;
  readonly battlePressureBar: BattlePressureBar;
  readonly battleDeadlineLine: BattleDeadlineLine;
  readonly guideQuestPanel: GuideQuestPanel;
  readonly battleDialogue: BattleDialogueOverlay;
  readonly bottomTab: BottomTabView;
  readonly tabContent: { x: number; y: number; width: number; height: number };
  readonly guideQuestRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  private readonly background: GameviewScrollingBackground;
  private readonly bgRect: GameviewBackgroundRect;
  private readonly frameNine: Phaser.GameObjects.NineSlice;
  private lowSpecRenderMode = false;
  private readonly fullGv: GameviewRect;
  private readonly panel: NineSlicePanel;
  private readonly folderTab: NineSliceButton;
  private readonly helpTab: NineSliceButton;
  private readonly layout: ShellLayout;
  private readonly screenW: number;
  private readonly playerShipFx: PlayerShipDestructionFx;
  private playerShipScale = 1;
  private playerShipSizeTweenTarget?: { size: number };

  constructor(private readonly scene: Phaser.Scene, deps: ShellFrameDeps) {
    const L = deps.layout;
    this.layout = L;
    const w = scene.scale.width;
    this.screenW = w;
    const cx = L.frameInsetX;
    const cw = w - L.frameInsetX * 2;
    const initialPanelRect = this.tabPanelRectFor(L.tabPanelTop, L.tabPanelHeight);
    // 게임뷰 (outer=9-slice 프레임 전체, interior=보더 14px 안쪽 — 배경 타일·cover 렌더 영역)
    const gv = computeGameviewRect(w, L);
    const bg = computeGameviewBackgroundRect(gv);
    this.fullGv = gv;
    this.bgRect = bg;

    const panel = new NineSlicePanel(scene, initialPanelRect.x, initialPanelRect.y, {
      style: CONTENT_PANEL_STYLE,
      width: initialPanelRect.width,
      height: initialPanelRect.height,
    });
    panel.setDepth(DEPTH.tabPanel);
    scene.add.existing(panel);
    this.panel = panel;

    const fullTabContent = resolvePanelContentRect(initialPanelRect, {
      border: CONTENT_PANEL_STYLE.border,
      contentPadding: CONTENT_PANEL_STYLE.contentPadding,
      innerPadX: TAB_INNER_PADDING_X,
      innerPadY: TAB_INNER_PADDING_Y,
    });
    // 가이드퀘스트는 HUD 바로 아래 게임뷰 상단 오버레이다. 탭 패널/게임뷰 높이를 reserve하지 않는다.
    this.guideQuestRect = this.guideQuestRectForGameview(gv);
    this.tabContent = fullTabContent;

    this.gameviewMask = new RectMask(scene, {
      x: gv.intX,
      y: gv.intTop,
      width: gv.intW,
      height: gv.intH,
    });
    this.background = new GameviewScrollingBackground(scene, bg, "gameview_bg_tile");
    this.frameNine = scene.add
      .nineslice(gv.outerX, gv.outerTop, "gameview_frame_9slice", undefined, gv.outerW, gv.outerH, 14, 14, 14, 14)
      .setOrigin(0, 0)
      .setDepth(DEPTH.frame);
    // 축소 우주선 (발사 원점 = 하단 중앙, layout.launcherY — safe-area 압축 반영. 발사 물리도 sceneLauncherY로 동일 값).
    // 시각 우주선은 고정 배치하고, 조준/발사체 방향은 BattlefieldRunner의 발사 계산만 따른다.
    this.launcherY0 = L.launcherY;
    this.launcherShip = scene.add
      .image(w / 2, L.launcherY, "spaceship_launcher")
      .setOrigin(0.5, 1)
      .setDisplaySize(LAUNCHER_SHIP_SIZE, LAUNCHER_SHIP_SIZE)
      .setDepth(DEPTH.launcher);
    this.playerShipFx = createPlayerShipDestructionFx(scene, this.launcherShip, this.gameviewMask, LAUNCHER_SHIP_SIZE);

    // 조준선 — 벽돌·발사체 뒤(depth 0), 배경 위.
    this.aimLine = new AimLineView(scene);
    this.aimLine.build();

    this.hud = new HudView(scene, cx, L.hudY, {
      onSettingsClick: deps.onSettingsClick,
      dir: getTextDirection(),
      accountNeedsLink: deps.accountNeedsLink,
    });

    this.dungeonTimerBar = new DungeonTimerBar(scene, gv.intX, gv.intTop + 2, gv.intW);
    this.battleDeadlineLine = new BattleDeadlineLine(
      scene,
      { x: gv.intX, y: gv.intTop, width: gv.intW, height: gv.intH },
      this.gameviewMask.mask,
    );
    this.battlePressureBar = new BattlePressureBar(
      scene,
      gv.intX + BATTLE_PRESSURE_RAIL_FRAME_OFFSET_X,
      gv.intTop + BATTLE_PRESSURE_RAIL_PAD_Y,
      Math.max(1, gv.intH - BATTLE_PRESSURE_RAIL_PAD_Y * 2),
    );
    this.guideQuestPanel = new GuideQuestPanel(
      scene,
      this.guideQuestRect.x,
      this.guideQuestRect.y,
      this.guideQuestRect.width,
      { onClaim: deps.onGuideQuestClaim, onGuide: deps.onGuideQuestGuide },
    );
    this.guideQuestPanel.setVisible(false);
    this.battleDialogue = new BattleDialogueOverlay(
      scene,
      this.dialogueRectForGameview(gv, 1),
      deps.onBattleDialogueComplete,
    );
    this.battleDialogue.setVisible(false);

    this.bottomTab = new BottomTabView(
      scene,
      cx,
      L.tabBarY,
      cw,
      L.tabBarHeight,
      L.tabBarRowGap,
      deps.onTabSelected,
      getTextDirection(),
      deps.enabledTabs,
    );

    // 폴더탭 펼치기/접기 버튼 — 패널 상단 모서리 우측에 탭처럼 삐져나옴(중앙 발사대 회피). 클릭 시 전역 토글.
    // depth는 콘텐츠(2~5)·패널(0) 위 — 패널 경계에서 항상 보이고 눌린다.
    this.folderTab = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("battle.expand"),
      widthOverride: FOLDER_TAB_W,
      heightOverride: FOLDER_TAB_H,
    });
    this.folderTab.setDepth(DEPTH.tabPanel + 12);
    scene.add.existing(this.folderTab);
    this.folderTab.on(NINE_SLICE_BUTTON_EVENT.Click, () => deps.onToggleExpand());
    this.positionFolderTab(L.tabPanelTop);

    // 도움말(?) 버튼 — 패널 좌상단 모서리(폴더탭과 대칭, RTL이면 우측). 클릭 시 현재 탭 튜토리얼 재생.
    this.helpTab = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label: "?",
      widthOverride: HELP_TAB_W,
      heightOverride: HELP_TAB_H,
    });
    this.helpTab.setDepth(DEPTH.tabPanel + 12);
    scene.add.existing(this.helpTab);
    this.helpTab.on(NINE_SLICE_BUTTON_EVENT.Click, () => deps.onHelp());
    this.positionHelpTab(L.tabPanelTop);
  }

  /** 폴더탭 버튼 위치 — 패널 상단 모서리 우측에서 위로 삐져나오게(절반 겹침). RTL이면 좌측. */
  private positionFolderTab(panelTop: number): void {
    const cw = this.screenW - this.layout.frameInsetX * 2;
    const left = this.layout.frameInsetX;
    const rtl = getTextDirection() === "rtl";
    const cxPos = rtl ? left + FOLDER_TAB_W / 2 + 8 : left + cw - FOLDER_TAB_W / 2 - 8;
    this.folderTab.setPosition(cxPos, panelTop - FOLDER_TAB_H / 2 + 3);
  }

  /** 도움말(?) 버튼 위치 — 패널 좌상단 모서리(폴더탭과 대칭). RTL이면 우측. */
  private positionHelpTab(panelTop: number): void {
    const cw = this.screenW - this.layout.frameInsetX * 2;
    const left = this.layout.frameInsetX;
    const rtl = getTextDirection() === "rtl";
    const cxPos = rtl ? left + cw - HELP_TAB_W / 2 - 8 : left + HELP_TAB_W / 2 + 8;
    this.helpTab.setPosition(cxPos, panelTop - HELP_TAB_H / 2 + 3);
  }

  /**
   * 전역 펼치기/접기 — 게임뷰 축소 + 탭 콘텐츠 패널 확장(위로) + 폴더탭/라벨 갱신. 모든 탭 공용(셸 소유).
   * 반환: { offset(발사대/콘텐츠 상향 N — GameScene이 registry/runner로 전파), content(새 탭 콘텐츠 rect — 활성 탭 resize 브로드캐스트) }.
   */
  setExpanded(expanded: boolean): { offset: number; content: TabContentRect } {
    const L = this.layout;
    const offset = this.setGameviewFraction(expanded ? SHELL_EXPAND_FRACTION : 1);
    const top = expanded ? computeExpandedTop(L, SHELL_EXPAND_FRACTION) : L.tabPanelTop;
    const height = expanded ? L.tabPanelBottom - top : L.tabPanelHeight;
    const panelRect = this.tabPanelRectFor(top, height);
    this.panel.setPosition(panelRect.x, panelRect.y).setSize(panelRect.width, panelRect.height);
    this.positionGuideQuestPanel(this.guideQuestRectForGameview(this.fullGv));
    this.positionFolderTab(panelRect.y);
    this.positionHelpTab(panelRect.y);
    this.folderTab.setLabel(expanded ? t("battle.collapse") : t("battle.expand"));
    const content = this.tabContentRectFor(expanded);
    this.copyTabContentRect(content);
    return { offset, content };
  }

  /**
   * 펼침/접힘 상태별 탭 콘텐츠 rect (부수효과 없음) — 탭 전환 시 새 활성 탭을 현재 전역 상태에 맞춰
   * 재배치(GameScene이 tabs.resize에 전달)하는 데 사용. setExpanded와 동일 산식(패널 9-slice inset).
   */
  tabContentRectFor(expanded: boolean): TabContentRect {
    const L = this.layout;
    const top = expanded ? computeExpandedTop(L, SHELL_EXPAND_FRACTION) : L.tabPanelTop;
    const height = expanded ? L.tabPanelBottom - top : L.tabPanelHeight;
    return this.fullContentRectFor(this.tabPanelRectFor(top, height));
  }

  updateGuideQuestPanel(data: GuideQuestPanelData): void {
    this.guideQuestPanel.update(data);
  }

  updateBattleDialogue(data: BattleDialogueData | null): void {
    this.battleDialogue.update(data);
  }

  playBattleDialogue(data: BattleDialogueData): void {
    this.battleDialogue.play(data);
  }

  setBattleDialogueVisible(visible: boolean): void {
    this.battleDialogue.setVisible(visible);
  }

  applyBattleDialogueLanguage(): void {
    this.battleDialogue.applyLanguage();
  }

  private tabPanelRectFor(top: number, height: number): TabContentRect {
    const L = this.layout;
    const cw = this.screenW - L.frameInsetX * 2;
    return { x: L.frameInsetX, y: top, width: cw, height };
  }

  private guideQuestRectForGameview(gv: GameviewRect): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    return computeGuideQuestPanelRect({
      x: gv.outerX,
      y: gv.outerTop,
      width: gv.outerW,
    });
  }

  private dialogueRectForGameview(gv: GameviewRect, fraction: number): DialogueOverlayRect {
    const top = Math.max(gv.intTop + 4, this.guideQuestRect.y + this.guideQuestRect.height + 4);
    const visibleBottom = gv.intTop + gv.intH * fraction;
    return {
      x: gv.intX,
      y: top,
      width: gv.intW,
      height: Math.max(0, visibleBottom - top),
    };
  }

  private fullContentRectFor(panelRect: TabContentRect): TabContentRect {
    return resolvePanelContentRect(panelRect, {
      border: CONTENT_PANEL_STYLE.border,
      contentPadding: CONTENT_PANEL_STYLE.contentPadding,
      innerPadX: TAB_INNER_PADDING_X,
      innerPadY: TAB_INNER_PADDING_Y,
    });
  }

  private positionGuideQuestPanel(rect: { x: number; y: number; width: number; height: number }): void {
    this.guideQuestRect.x = rect.x;
    this.guideQuestRect.y = rect.y;
    this.guideQuestRect.width = rect.width;
    this.guideQuestRect.height = rect.height;
    this.guideQuestPanel.setLayout(rect.x, rect.y, rect.width);
  }

  private copyTabContentRect(rect: TabContentRect): void {
    this.tabContent.x = rect.x;
    this.tabContent.y = rect.y;
    this.tabContent.width = rect.width;
    this.tabContent.height = rect.height;
  }

  /** 보상 fly-to 타겟 좌표 + 아이콘 키 — 골드/다이아=HUD 칩, artifact=유물 탭. 유물 탭 비활성이면 null. */
  getRewardTargetPos(kind: "gold" | "diamond" | "artifact"): { x: number; y: number; iconKey: string } | null {
    if (kind === "gold") {
      const p = this.hud.goldChipPos;
      return { x: p.x, y: p.y, iconKey: "icon_gold" };
    }
    if (kind === "diamond") {
      const p = this.hud.diamondChipPos;
      return { x: p.x, y: p.y, iconKey: "icon_diamond" };
    }
    const p = this.bottomTab.relicTabPos();
    return p ? { x: p.x, y: p.y, iconKey: "icon_tab_relic" } : null;
  }

  setAccountNeedsLink(value: boolean): void {
    this.hud.setAccountNeedsLink(value);
  }

  setWorldVisual(world: ShellWorldVisual): void {
    this.background.setTexture(world.bgTileKey);
  }

  setPlayerShipTexture(textureKey: string, shipScale = 1): void {
    const nextScale = Number.isFinite(shipScale) && shipScale > 0 ? shipScale : 1;
    const sameTexture = this.launcherShip.texture.key === textureKey;
    const sameScale = Math.abs(this.playerShipScale - nextScale) < 0.001;
    if (sameTexture && sameScale) return;
    if (!sameTexture) this.launcherShip.setTexture(textureKey);
    this.playerShipScale = nextScale;
    this.applyPlayerShipSize(true);
  }

  setLowSpecRenderMode(enabled: boolean): void {
    if (this.lowSpecRenderMode === enabled) return;
    this.lowSpecRenderMode = enabled;
    this.background.setEnabled(!enabled);
    this.aimLine.setEnabled(!enabled);
  }

  updateBackgroundScroll(deltaMs: number): void {
    this.background.update(deltaMs);
  }

  get defeatDeadlineY(): number {
    return this.launcherShip.y - this.launcherShip.displayHeight;
  }

  playPlayerShipDestruction(): void {
    this.aimLine.setEnabled(false);
    this.playerShipFx.play();
  }

  restorePlayerShip(): void {
    this.playerShipFx.restore();
    this.applyPlayerShipSize();
    this.aimLine.setEnabled(!this.lowSpecRenderMode);
  }

  private applyPlayerShipSize(animate = false): void {
    const size = LAUNCHER_SHIP_SIZE * this.playerShipScale;
    if (!animate) {
      this.cancelPlayerShipSizeTween();
      this.launcherShip.setDisplaySize(size, size);
      return;
    }
    this.cancelPlayerShipSizeTween();
    const tweenTarget = { size: this.launcherShip.displayWidth || LAUNCHER_SHIP_SIZE };
    this.playerShipSizeTweenTarget = tweenTarget;
    this.scene.tweens.add({
      targets: tweenTarget,
      size,
      duration: PLAYER_SHIP_SCALE_TWEEN_MS,
      ease: "Sine.easeInOut",
      onUpdate: () => this.launcherShip.setDisplaySize(tweenTarget.size, tweenTarget.size),
      onComplete: () => {
        this.launcherShip.setDisplaySize(size, size);
        if (this.playerShipSizeTweenTarget === tweenTarget) this.playerShipSizeTweenTarget = undefined;
      },
    });
  }

  private cancelPlayerShipSizeTween(): void {
    if (!this.playerShipSizeTweenTarget) return;
    this.scene.tweens.killTweensOf(this.playerShipSizeTweenTarget);
    this.playerShipSizeTweenTarget = undefined;
  }

  /**
   * 게임뷰를 f(0~1) 비율 높이로 시각 클립하고, 축소 우주선(+조준선)을 "좁혀진 만큼" 위로 끌어올려
   * 좁아진 게임뷰 하단에 앉힌다 — 전투탭 펼치기 전용. 반환값 = 발사 원점 상향 오프셋 N(=좁혀진 interior 높이).
   *
   * 창(배경 타일·9-slice 프레임·마스크)은 상단 기준으로 축소(좌상단 고정, 비율 왜곡 없음).
   * 배경은 프레임 안쪽을 거의 채우도록 visual inset만 남기고, 마스크/물리 경계는 기존 interior를 유지한다.
   * 축소 우주선은 `launcherY0-N`으로 이동 + 마스킹(펼침 시), 접기(f=1, N=0)면 원위치·마스크 해제.
   * 벽돌/발사체/playfieldBounds는 불변 — 발사 원점만 sceneLauncherY가 N을 반영(GameScene이 registry 설정).
   * 발사체는 spawn 시 마스크가 적용돼 창 밖(하단 절반)은 가려지고, 하단 벽돌은 바운드 내라 여전히 처치 가능.
   */
  setGameviewFraction(f: number): number {
    const gv = this.fullGv;
    const bgVisibleH = Math.max(0, gv.outerH * f - GAMEVIEW_BACKGROUND_INSET * 2);
    this.background.resize(this.bgRect.width, bgVisibleH);
    this.frameNine.setSize(gv.outerW, gv.outerH * f);
    this.gameviewMask.setRect({
      x: gv.intX,
      y: gv.intTop,
      width: gv.intW,
      height: gv.intH * f,
    });
    this.battleDeadlineLine.setLayout({
      x: gv.intX,
      y: gv.intTop,
      width: gv.intW,
      height: gv.intH * f,
    });
    this.battlePressureBar.setLayout(
      gv.intX + BATTLE_PRESSURE_RAIL_FRAME_OFFSET_X,
      gv.intTop + BATTLE_PRESSURE_RAIL_PAD_Y,
      Math.max(1, gv.intH * f - BATTLE_PRESSURE_RAIL_PAD_Y * 2),
    );
    this.battleDialogue.resize(this.dialogueRectForGameview(gv, f));
    // 발사대 상향 오프셋 N — 축소 우주선이 축소된 창 [intTop, intTop+intH·f] 안에 **완전히** 보이도록
    // 우주선 하단 기준점이 창 하단 이하가 되게 올린다(일부 클립 방지). f=1(접기)이면 0(원복).
    // 벽돌·발사체·playfieldBounds·발사 원점도 같은 N으로 이동(난이도 보존) — GameScene이 registry로 전파.
    const windowBottom = gv.intTop + gv.intH * f;
    const n = f >= 1 ? 0 : Math.max(0, this.launcherY0 - windowBottom + 2);
    this.launcherShip.setY(this.launcherY0 - n);
    // 펼침이면 우주선·조준선을 게임뷰 마스크로 클립(창 밖 새지 않게), 접기면 해제.
    if (n > 0) {
      this.launcherShip.setMask(this.gameviewMask.mask);
    } else {
      this.launcherShip.clearMask();
    }
    this.aimLine.setMask(n > 0 ? this.gameviewMask.mask : null);
    return n;
  }

  /** SHUTDOWN 해제 — 마스크는 GeometryMask 리소스라 명시 destroy (나머지 표시 객체는 씬 파괴가 수거). */
  dispose(): void {
    this.gameviewMask.destroy();
  }
}
